import type {
  DataProvider,
  BaseRecord,
  GetListResponse,
  GetOneResponse,
  CreateResponse,
  UpdateResponse,
  DeleteOneResponse,
  GetManyResponse,
  CustomResponse,
  BaseKey,
  HttpError,
  CrudFilter,
  LogicalFilter,
  GetListParams,
} from '@refinedev/core';
import type { ApiResponse } from '../types';
import { resolveApiUrl } from './api-url';

const API_URL = resolveApiUrl();

/**
 * Получить заголовки авторизации с JWT токеном
 * Глобальный интерцептор автоматически добавляет Authorization,
 * но здесь мы также добавляем Content-Type
 */
function getAuthHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

type ErrorMetaFields = {
  fields?: Record<string, string[]>;
};

function formatErrorMessage(errors: unknown): string | null {
  if (!Array.isArray(errors)) {
    return null;
  }

  const messages = errors
    .filter((error): error is string => typeof error === 'string')
    .map((error) => error.trim())
    .filter((error) => error.length > 0);

  if (messages.length === 0) {
    return null;
  }

  return messages.join('\n');
}

/**
 * Форматирует ошибки валидации по полям в читаемое сообщение
 */
function formatFieldErrors(fields: Record<string, string[]> | undefined): string | null {
  if (!fields || typeof fields !== 'object') {
    return null;
  }

  const fieldMessages: string[] = [];

  for (const [field, messages] of Object.entries(fields)) {
    if (Array.isArray(messages) && messages.length > 0) {
      // Форматируем как "поле: ошибка"
      for (const msg of messages) {
        fieldMessages.push(`${field}: ${msg}`);
      }
    }
  }

  return fieldMessages.length > 0 ? fieldMessages.join('\n') : null;
}

function extractFieldErrors(meta: ApiResponse<unknown>['meta']): HttpError['errors'] | undefined {
  const fields = (meta as ErrorMetaFields | null | undefined)?.fields;

  if (!fields || typeof fields !== 'object') {
    return undefined;
  }

  return fields as HttpError['errors'];
}

function buildHttpErrorFromPayload(
  statusCode: number,
  statusText: string,
  payload?: ApiResponse<unknown> | null,
): HttpError {
  const fields = extractFieldErrors(payload?.meta ?? null);
  const fieldErrorsMessage = formatFieldErrors(fields as Record<string, string[]> | undefined);

  // Формируем сообщение: сначала общая ошибка, потом детали по полям
  let message = formatErrorMessage(payload?.errors) ?? `HTTP ${statusCode}: ${statusText}`;

  // Если есть детальные ошибки по полям, добавляем их
  if (fieldErrorsMessage) {
    // Если общее сообщение содержит "Ошибка валидации", используем более понятный заголовок
    const isValidationError =
      message.toLowerCase().includes('валидаци') ||
      (payload?.meta as ErrorMetaFields | null | undefined)?.fields;

    message = isValidationError
      ? `Ошибка валидации\n${fieldErrorsMessage}`
      : `${message}\n${fieldErrorsMessage}`;
  }

  return {
    message,
    statusCode,
    status: statusCode,
    errors: fields,
  };
}

async function buildHttpError(response: Response): Promise<HttpError> {
  let payload: ApiResponse<unknown> | null = null;

  try {
    payload = (await response.json()) as ApiResponse<unknown> | null;
  } catch {
    // ignore body parsing errors
  }

  return buildHttpErrorFromPayload(response.status, response.statusText, payload);
}

/**
 * Build query string from params
 */
function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Map resource name to API endpoint
 * Some resources have different API paths
 */
function getResourceEndpoint(resource: string): string {
  const resourceMap: Record<string, string> = {
    // Core
    'admin-users': 'admin-users',
    users: 'users',
    roles: 'rbac/roles',
    permissions: 'rbac/permissions',

    // Tags & UTM Sources
    tags: 'tags',
    'utm-sources': 'tags/utm-sources',

    // Leads CRM
    leads: 'leads',

    // Subscriptions
    subscriptions: 'subscriptions',
    'subscription-plans': 'subscription-plans/admin',
    'subscription-benefits': 'subscription-benefits',

    // Channel Passes
    'channel-passes': 'channel-passes/admin',
    'pricing-tiers': 'pricing-tiers/admin',

    // Courses
    courses: 'courses',
    modules: 'modules',
    'module-items': 'module-items',

    // Library
    books: 'admin/books',

    // Content
    banners: 'banners',
    announcements: 'announcements',
    'mini-banners': 'mini-banners',
    articles: 'articles',
    teachers: 'teachers',

    // Localization
    'ui-translations': 'translations/ui',
    'translation-keys/ui': 'translations/ui',
    'translation-keys/bot': 'translations/bot',
    languages: 'languages',
    'admin/languages': 'admin/languages',

    // Notifications
    triggers: 'admin/triggers',
    broadcasts: 'admin/broadcasts',
    'message-templates': 'admin/message-templates',

    // Other
    payments: 'payments',
    'knowledge-documents': 'knowledge-base',
    'support-tickets': 'support-tickets',
    'daily-stats': 'analytics/daily-stats',
    'telegram-channels': 'telegram-channels',
    'channel-memberships': 'channel-memberships',
    'bot-tokens': 'telegram-bots',
    integrations: 'integrations/services',
    'audit-logs': 'audit-logs',

    // Currency
    currencies: 'currencies',
    'admin/currencies': 'admin/currencies',

    // Social / Moderation
    posts: 'admin/posts',
    reports: 'admin/reports',
    moderation: 'admin/moderation',
    violations: 'admin/violations',
    'chat-moderation/rooms': 'admin/chat-moderation/rooms',
    'game-moderation/lobbies': 'admin/game-moderation/lobbies',
    'game-moderation/sessions': 'admin/game-moderation/sessions',

    // Bonuses
    'user-bonuses': 'user-bonuses/admin',

    // Club
    'club-settings': 'admin/club-settings',

    // User Entitlements
    'user-entitlements': 'users/entitlements',

    // Livestreams
    livestreams: 'admin/livestreams',

    // Promo Codes
    'promo-codes': 'promo-codes',

    // Audience Segments (saved UserSegments)
    'audience-segments': 'audience/segments',
  };

  return resourceMap[resource] ?? resource;
}

type CreateRequest = {
  url: string;
  payload: unknown;
};

function resolveCreateRequest(resource: string, variables: unknown): CreateRequest {
  if (resource === 'modules') {
    const { courseId, ...payload } = (variables ?? {}) as Record<string, unknown>;
    if (!courseId) {
      throw new Error('Необходимо указать courseId для создания модуля');
    }
    return {
      url: `${API_URL}/courses/${courseId}/modules`,
      payload,
    };
  }

  if (resource === 'module-items') {
    const { moduleId, ...payload } = (variables ?? {}) as Record<string, unknown>;
    if (!moduleId) {
      throw new Error('Необходимо указать moduleId для создания урока');
    }
    return {
      url: `${API_URL}/modules/${moduleId}/items`,
      payload,
    };
  }

  if (resource === 'subscription-benefits') {
    const { planId, ...payload } = (variables ?? {}) as Record<string, unknown>;
    if (!planId) {
      throw new Error('Необходимо указать planId для создания преимущества подписки');
    }
    return {
      url: `${API_URL}/subscription-plans/${planId}/benefits`,
      payload,
    };
  }

  const endpoint = getResourceEndpoint(resource);
  return {
    url: `${API_URL}/${endpoint}`,
    payload: variables,
  };
}

function normalizeTriggerPayload(variables: unknown): unknown {
  if (!variables || typeof variables !== 'object') {
    return variables;
  }
  const { delayMinutes, delay, ...rest } = variables as Record<string, unknown>;

  return {
    ...rest,
    delay: delay ?? delayMinutes ?? 0,
  };
}

function normalizeTriggerResponse<TData>(data: TData): TData {
  if (!data || typeof data !== 'object') {
    return data;
  }
  const record = data as Record<string, unknown>;
  if ('delay' in record && !('delayMinutes' in record)) {
    return { ...record, delayMinutes: record.delay ?? 0 } as TData;
  }
  return data;
}

/**
 * API возвращает { settings: {...}, translations: [...] },
 * а форма ожидает плоский объект { ...settings, translations }
 */
function normalizeClubSettingsResponse<TData>(data: TData): TData {
  if (!data || typeof data !== 'object') {
    return data;
  }
  const record = data as Record<string, unknown>;
  if ('settings' in record && 'translations' in record) {
    const settings = record.settings as Record<string, unknown> | null;
    if (settings) {
      return { ...settings, translations: record.translations } as TData;
    }
    // settings = null — ещё не создано, возвращаем пустой объект с id для формы
    return { id: 'singleton', translations: record.translations } as TData;
  }
  return data;
}

const SUBSCRIPTION_STATUS_MAP: Record<string, string> = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
};

function normalizeSubscriptionResponse<TData>(data: TData): TData {
  if (!data || typeof data !== 'object') {
    return data;
  }
  const record = data as Record<string, unknown>;
  const status = record.status as string | undefined;
  const mappedStatus = status ? (SUBSCRIPTION_STATUS_MAP[status] ?? status.toLowerCase()) : status;
  const amount = record.amount;
  const price =
    record.price ??
    (typeof amount === 'number' ? amount : (typeof amount === 'string' ? Number(amount) : amount));
  return {
    ...record,
    status: mappedStatus,
    price,
  } as TData;
}

const SUPPORT_TICKET_STATUS_MAP: Record<string, string> = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
};

function normalizeSupportTicketResponse<TData>(data: TData): TData {
  if (!data || typeof data !== 'object') {
    return data;
  }
  const record = data as Record<string, unknown>;
  const question = record.question;
  const status = record.status as string | undefined;
  const mappedStatus = status
    ? (SUPPORT_TICKET_STATUS_MAP[status] ?? status.toLowerCase())
    : status;
  const assignedAdmin = record.assignedAdmin ?? record.assignedTo ?? null;
  const assignedAdminId = record.assignedAdminId ?? record.assignedToId ?? null;
  return {
    ...record,
    subject: record.subject ?? (typeof question === 'string' ? question : 'Без темы'),
    priority: record.priority ?? 'medium',
    status: mappedStatus,
    assignedToId: assignedAdminId,
    assignedTo: assignedAdmin,
    updatedAt: record.updatedAt ?? record.createdAt ?? null,
  } as TData;
}

const PAYMENT_STATUS_MAP: Record<string, string> = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  REJECTED: 'rejected',
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'partially_refunded',
};

function normalizePaymentResponse<TData>(data: TData): TData {
  if (!data || typeof data !== 'object') {
    return data;
  }
  const record = data as Record<string, unknown>;
  const status = record.status as string | undefined;
  const mappedStatus = status ? (PAYMENT_STATUS_MAP[status] ?? status.toLowerCase()) : status;
  return {
    ...record,
    status: mappedStatus,
    provider: record.provider ?? 'manual',
    providerPaymentId: record.providerPaymentId ?? record.providerId ?? null,
    subscriptionId: record.subscriptionId ?? null,
  } as TData;
}

function normalizeStatusFilter(resource: string, field: string, value: string): string {
  if (field !== 'status') {
    return value;
  }
  if (
    resource === 'subscriptions' ||
    resource === 'support-tickets' ||
    resource === 'payments' ||
    resource === 'orders'
  ) {
    return value.toUpperCase();
  }
  return value;
}

function isLogicalFilter(filter: CrudFilter): filter is LogicalFilter {
  return 'field' in filter && typeof filter.field === 'string';
}

/**
 * Распознаёт data-обёртку вида { items: TData[]; total?: number } — её отдают
 * эндпоинты, оборачивающие список через ResponseFormatter.success({ items, total })
 * (например audience/segments). Type guard через `in`, без кастов.
 */
function isItemsEnvelope<TData>(value: unknown): value is { items: TData[]; total?: number } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'items' in value &&
    Array.isArray(value.items)
  );
}

/**
 * Custom DataProvider for Masters Club Backend API
 * Adapts Refine's data layer to our backend response format
 */
export const dataProvider: DataProvider = {
  /**
   * Get list of records with pagination, filtering, and sorting
   */
  getList: async <TData extends BaseRecord = BaseRecord>(
    params: GetListParams,
  ): Promise<GetListResponse<TData>> => {
    const { resource, pagination, filters, sorters } = params;
    const endpoint = getResourceEndpoint(resource);
    const paginationMode = pagination?.mode ?? 'server';
    const currentPage = pagination?.currentPage ?? 1;
    const pageSize = pagination?.pageSize ?? 10;

    // Build query params
    // When pagination mode is 'off', don't send page/limit params
    const queryParams: Record<string, string | number | boolean | undefined> = {};
    if (paginationMode !== 'off') {
      // Some resources use offset-based pagination instead of page-based
      const useOffsetPagination = ['reports', 'moderation', 'livestreams'].includes(resource);
      if (useOffsetPagination) {
        queryParams.offset = (currentPage - 1) * pageSize;
        queryParams.limit = pageSize;
      } else {
        queryParams.page = currentPage;
        queryParams.limit = pageSize;
      }
    }

    // Add filters
    if (filters && filters.length > 0) {
      for (const filter of filters) {
        if (isLogicalFilter(filter) && filter.value !== undefined) {
          // Handle 'in' operator — serialize array as comma-separated string
          if (filter.operator === 'in') {
            if (Array.isArray(filter.value) && filter.value.length > 0) {
              const isPrimitiveArray = (v: unknown): v is (string | number)[] =>
                Array.isArray(v) && v.every((x) => typeof x === 'string' || typeof x === 'number');
              queryParams[filter.field] = isPrimitiveArray(filter.value)
                ? filter.value.map(String).join(',')
                : filter.value.map((x) => String(x)).join(',');
            }
            continue;
          }
          const normalizedValue =
            typeof filter.value === 'string'
              ? normalizeStatusFilter(resource, filter.field, filter.value)
              : filter.value;
          if (
            typeof normalizedValue === 'string' ||
            typeof normalizedValue === 'number' ||
            typeof normalizedValue === 'boolean'
          ) {
            queryParams[filter.field] = normalizedValue;
          }
        }
      }
    }

    // Add sorters
    if (sorters && sorters.length > 0) {
      const sorter = sorters[0];
      queryParams.sortBy = sorter.field;
      queryParams.sortOrder = sorter.order;
    }

    const queryString = buildQueryString(queryParams);
    const url = `${API_URL}/${endpoint}${queryString}`;

    const headers = getAuthHeaders();
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const json = (await response.json()) as ApiResponse<TData[]> | { data: TData[]; total: number };

    // Handle both formats: { success, data, meta } and { data, total }
    const hasSuccessField = 'success' in json;
    if (hasSuccessField && !json.success) {
      throw buildHttpErrorFromPayload(
        response.status,
        response.statusText,
        json as ApiResponse<TData[]>,
      );
    }

    // Часть эндпоинтов отдаёт data в форме { items, total } вместо голого
    // массива (audience/segments → ResponseFormatter.success({ items, total })).
    // Разворачиваем в плоский массив, чтобы useList получил data: TData[].
    const rawData: unknown = json.data ?? [];
    let envelopeTotal: number | null = null;
    let data: TData[];
    if (isItemsEnvelope<TData>(rawData)) {
      envelopeTotal = typeof rawData.total === 'number' ? rawData.total : null;
      data = rawData.items;
    } else if (Array.isArray(rawData)) {
      data = rawData;
    } else {
      data = [];
    }
    // Get total from either meta.total or top-level total
    const total: number =
      envelopeTotal ??
      ('meta' in json && typeof json.meta?.total === 'number' ? json.meta.total : null) ??
      ('total' in json && typeof json.total === 'number' ? json.total : data.length);

    if (resource === 'triggers') {
      data = data.map((item) => normalizeTriggerResponse(item));
    }

    if (resource === 'subscriptions') {
      data = data.map((item) => normalizeSubscriptionResponse(item));
    }

    if (resource === 'support-tickets') {
      data = data.map((item) => normalizeSupportTicketResponse(item));
    }

    if (resource === 'payments') {
      data = data.map((item) => normalizePaymentResponse(item));
    }

    return {
      data,
      total,
    };
  },

  /**
   * Get single record by ID
   */
  getOne: async <TData extends BaseRecord = BaseRecord>({
    resource,
    id,
  }: {
    resource: string;
    id: BaseKey;
  }): Promise<GetOneResponse<TData>> => {
    const endpoint = getResourceEndpoint(resource);
    const url = `${API_URL}/${endpoint}/${id}`;

    const headers = getAuthHeaders();
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const json: ApiResponse<TData> = await response.json();

    if (!json.success) {
      throw buildHttpErrorFromPayload(response.status, response.statusText, json);
    }

    if (!json.data) {
      throw new Error('Запись не найдена');
    }

    let data = json.data;

    if (resource === 'triggers') {
      data = normalizeTriggerResponse(data);
    }

    if (resource === 'subscriptions') {
      data = normalizeSubscriptionResponse(data);
    }

    if (resource === 'support-tickets') {
      data = normalizeSupportTicketResponse(data);
    }

    if (resource === 'payments') {
      data = normalizePaymentResponse(data);
    }

    if (resource === 'club-settings') {
      data = normalizeClubSettingsResponse(data);
    }

    return { data };
  },

  /**
   * Create new record
   */
  create: async <TData extends BaseRecord = BaseRecord, TVariables = unknown>({
    resource,
    variables,
  }: {
    resource: string;
    variables: TVariables;
  }): Promise<CreateResponse<TData>> => {
    let createVariables: unknown = variables;

    if (resource === 'triggers') {
      createVariables = normalizeTriggerPayload(variables);
    }

    const { url, payload } = resolveCreateRequest(resource, createVariables);

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const json: ApiResponse<TData> = await response.json();

    if (!json.success) {
      throw buildHttpErrorFromPayload(response.status, response.statusText, json);
    }

    if (!json.data) {
      throw new Error('Созданные данные не вернулись');
    }

    let data = json.data;

    if (resource === 'triggers') {
      data = normalizeTriggerResponse(data);
    }

    return { data };
  },

  /**
   * Update existing record
   */
  update: async <TData extends BaseRecord = BaseRecord, TVariables = unknown>({
    resource,
    id,
    variables,
  }: {
    resource: string;
    id: BaseKey;
    variables: TVariables;
  }): Promise<UpdateResponse<TData>> => {
    const endpoint = getResourceEndpoint(resource);
    const headers = getAuthHeaders();

    let updateVariables: unknown = variables;

    if (resource === 'triggers') {
      updateVariables = normalizeTriggerPayload(variables);
    }

    const url = `${API_URL}/${endpoint}/${id}`;

    // Эти backend endpoints используют PATCH для частичных обновлений.
    const method = resource === 'livestreams' || resource === 'utm-sources' || resource === 'promo-codes' || resource === 'tags' ? 'PATCH' : 'PUT';

    const response = await fetch(url, {
      method,
      headers,
      body: JSON.stringify(updateVariables),
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const json: ApiResponse<TData> = await response.json();

    if (!json.success) {
      throw buildHttpErrorFromPayload(response.status, response.statusText, json);
    }

    if (!json.data) {
      throw new Error('Обновленные данные не вернулись');
    }

    let data = json.data;

    if (resource === 'triggers') {
      data = normalizeTriggerResponse(data);
    }

    if (resource === 'club-settings') {
      data = normalizeClubSettingsResponse(data);
    }

    return { data };
  },

  /**
   * Delete record by ID
   */
  deleteOne: async <TData extends BaseRecord = BaseRecord>({
    resource,
    id,
  }: {
    resource: string;
    id: BaseKey;
  }): Promise<DeleteOneResponse<TData>> => {
    const endpoint = getResourceEndpoint(resource);
    const url = `${API_URL}/${endpoint}/${id}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    if (response.status === 204) {
      return { data: { id } as TData };
    }

    const json: ApiResponse<TData> = await response.json();

    if (!json.success) {
      throw buildHttpErrorFromPayload(response.status, response.statusText, json);
    }

    if (json.data) {
      return { data: json.data };
    }
    return { data: { id } as TData };
  },

  /**
   * Get multiple records by IDs
   */
  getMany: async <TData extends BaseRecord = BaseRecord>({
    resource,
    ids,
  }: {
    resource: string;
    ids: BaseKey[];
  }): Promise<GetManyResponse<TData>> => {
    const endpoint = getResourceEndpoint(resource);

    const promises = ids.map(async (id) => {
      const url = `${API_URL}/${endpoint}/${id}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw await buildHttpError(response);
        }
        return null;
      }

      const json: ApiResponse<TData> = await response.json();
      return json.success ? (json.data ?? null) : null;
    });

    const results = await Promise.all(promises);
    const data = results.filter(
      (item): item is NonNullable<typeof item> => item !== null,
    ) as TData[];

    return { data };
  },

  /**
   * Custom API call for non-standard endpoints
   */
  custom: async <TData extends BaseRecord = BaseRecord, TQuery = unknown, TPayload = unknown>({
    url,
    method,
    payload,
    query,
    headers,
  }: {
    url: string;
    method: 'get' | 'post' | 'put' | 'patch' | 'delete' | 'head' | 'options';
    payload?: TPayload;
    query?: TQuery;
    headers?: Record<string, string>;
  }): Promise<CustomResponse<TData>> => {
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;

    let queryString = '';
    if (query && typeof query === 'object') {
      queryString = buildQueryString(
        query as Record<string, string | number | boolean | undefined>,
      );
    }

    const response = await fetch(`${fullUrl}${queryString}`, {
      method: method.toUpperCase(),
      headers: {
        ...getAuthHeaders(),
        ...headers,
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const json: ApiResponse<TData> = await response.json();

    if (typeof json === 'object' && json && 'success' in json && json.success === false) {
      throw buildHttpErrorFromPayload(
        response.status,
        response.statusText,
        json as ApiResponse<unknown>,
      );
    }

    return { data: (json.data ?? json) as TData };
  },

  /**
   * Get API URL
   */
  getApiUrl: () => API_URL,
};
